import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import CloudOutlinedIcon from '@mui/icons-material/CloudOutlined'
import InsightsOutlinedIcon from '@mui/icons-material/InsightsOutlined'
import SmartToyOutlinedIcon from '@mui/icons-material/SmartToyOutlined'
import {
  Alert,
  Box,
  CircularProgress,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
} from '@mui/material'
import React, { useState } from 'react'
import { useIntl } from 'react-intl'
import type { AgentOption, CaBackend } from '../../shared/types'

const BACKEND_ICON: Record<CaBackend, React.ReactElement> = {
  gcp: <CloudOutlinedIcon />,
  looker: <InsightsOutlinedIcon />,
}

export default function AgentPicker(
  { backends, agents, onPick, pending = false, loading = false, error }:
  {
    backends: CaBackend[]
    agents: AgentOption[]
    onPick: (backend: CaBackend, agent?: AgentOption) => void
    pending?: boolean
    loading?: boolean
    error?: string
  },
) {
  const intl = useIntl()
  const BACKEND_LABEL: Record<CaBackend, string> = {
    gcp: intl.formatMessage({ id: 'caChat.picker.backend.gcp' }),
    looker: intl.formatMessage({ id: 'caChat.picker.backend.looker' }),
  }
  const [backendChoice, setBackendChoice] = useState<CaBackend | null>(null)
  const errorBanner = error ? (
    <Alert severity="error" sx={{ mb: 1 }}>{error}</Alert>
  ) : null

  if (loading) {
    return (
      <Box sx={{ p: 3, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress size={22} />
      </Box>
    )
  }
  if (!backends.length) {
    return (
      <Box sx={{ p: 3, textAlign: 'center', color: 'text.secondary' }}>
        <Typography variant="body2">
          {intl.formatMessage({ id: 'caChat.picker.noAgents' })}
        </Typography>
      </Box>
    )
  }

  const groupOf = (backend: CaBackend) => agents.filter((a) => a.backend === backend)

  const chooseBackend = (backend: CaBackend) => {
    if (pending) { return }
    const group = groupOf(backend)
    if (group.length > 1) { setBackendChoice(backend); return }
    onPick(backend, group[0])
  }

  if (backendChoice) {
    return (
      <Box sx={{ px: 2, py: 1, overflowY: 'auto' }}>
        {errorBanner}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, my: 1 }}>
          <IconButton
            aria-label={intl.formatMessage({ id: 'caChat.picker.aria.back' })}
            size="small"
            onClick={() => setBackendChoice(null)}
          >
            <ArrowBackIcon fontSize="small" />
          </IconButton>
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
            {intl.formatMessage({ id: 'caChat.picker.step2.heading' }, { backend: BACKEND_LABEL[backendChoice] })}
          </Typography>
        </Box>
        <List dense={true} disablePadding={true}>
          {groupOf(backendChoice).map((agent) => (
            <ListItemButton
              key={agent.id}
              disabled={pending}
              onClick={() => { if (!pending) { onPick(backendChoice, agent) } }}
              sx={{ borderRadius: 1 }}
            >
              <ListItemIcon><SmartToyOutlinedIcon fontSize="small" /></ListItemIcon>
              <ListItemText primary={agent.name} secondary={agent.description} />
            </ListItemButton>
          ))}
        </List>
      </Box>
    )
  }

  return (
    <Box sx={{ px: 2, py: 1, overflowY: 'auto' }}>
      {errorBanner}
      <Typography variant="subtitle1" sx={{ fontWeight: 600, textAlign: 'center', my: 1 }}>
        {intl.formatMessage({ id: 'caChat.picker.heading' })}
      </Typography>
      <Typography variant="body2" sx={{ textAlign: 'center', color: 'text.secondary', mb: 1 }}>
        {intl.formatMessage({ id: 'caChat.picker.subheading' })}
      </Typography>
      <List dense={true} disablePadding={true}>
        {backends.map((backend) => {
          const group = groupOf(backend)
          const summary =
            group.length > 1
              ? intl.formatMessage({ id: 'caChat.picker.agentSummary.count' }, { count: group.length })
              : group[0]?.name ?? intl.formatMessage({ id: 'caChat.picker.agentSummary.default' })
          return (
            <ListItemButton
              key={backend}
              disabled={pending}
              onClick={() => chooseBackend(backend)}
              sx={{ borderRadius: 1 }}
            >
              <ListItemIcon>{BACKEND_ICON[backend]}</ListItemIcon>
              <ListItemText primary={BACKEND_LABEL[backend]} secondary={summary} />
            </ListItemButton>
          )
        })}
      </List>
    </Box>
  )
}
